import { useState, type FormEvent } from 'react';

// ============================================================================
// Отправка формы лид-магнита.
// Backend (Cloudflare Worker) уже готов и протестирован — здесь только frontend.
// Данные уходят одним POST-запросом, нигде не сохраняются и не логируются.
// ============================================================================
const WORKER_URL =
  'https://sistema-razvitiya-lead-worker.aleksey-nsp.workers.dev/';

// Базовая клиентская проверка формата email (финальную проверку всё равно делает сервер).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUCCESS_MESSAGE =
  'Готово! Мы отправили письмо с подтверждением на вашу почту. Откройте его и нажмите на кнопку в письме — тогда придёт ссылка на пособие. Если письма нет несколько минут, загляните в «Спам» или «Промоакции».';

const ERROR_MESSAGE =
  'Не получилось отправить. Попробуйте ещё раз через минуту или напишите на info@business-training.by';

type Status = 'idle' | 'submitting' | 'success' | 'error';

type YandexMetrica = (id: number, action: string, goal?: string) => void;

function reachGoal(goal: string) {
  const w = window as unknown as { ym?: YandexMetrica };
  if (w.ym) {
    w.ym(106079874, 'reachGoal', goal);
  }
}

interface FieldErrors {
  name: boolean;
  email: boolean;
  consent: boolean;
}

export default function LeadMagnetForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [consentTransfer, setConsentTransfer] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<FieldErrors>({
    name: false,
    email: false,
    consent: false,
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Один POST-запрос за раз: пока предыдущий не завершился, повторный клик игнорируем.
    if (status === 'submitting') {
      return;
    }

    const nextErrors: FieldErrors = {
      name: name.trim().length === 0,
      email: !EMAIL_RE.test(email.trim()),
      consent: !consentTransfer,
    };
    setErrors(nextErrors);
    if (nextErrors.name || nextErrors.email || nextErrors.consent) {
      return;
    }

    setStatus('submitting');

    try {
      const response = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          consent_transfer: consentTransfer,
          lead_hp: honeypot,
        }),
      });

      // Реальный текст ошибки сервер не отдаёт — различаем только «успех / не успех».
      let data: { ok?: boolean } | null = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.ok && data !== null && data.ok === true) {
        setStatus('success');
        reachGoal('leadmagnet_success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  // Успех: форма скрывается, вместо неё — сообщение.
  if (status === 'success') {
    return (
      <div className="bg-teal-50 border border-teal-100 rounded-xl p-6 md:p-7 text-center">
        <div className="w-12 h-12 mx-auto mb-4 bg-teal-600 rounded-full flex items-center justify-center">
          <i className="ri-mail-check-line text-2xl text-white"></i>
        </div>
        <p className="text-sm md:text-base text-gray-700 leading-relaxed">
          {SUCCESS_MESSAGE}
        </p>
      </div>
    );
  }

  const submitting = status === 'submitting';

  const inputClass = (invalid: boolean) =>
    `w-full px-4 py-3 rounded-lg border text-sm text-gray-900 placeholder-gray-400 outline-none transition-colors ${
      invalid
        ? 'border-red-400 bg-red-50 focus:border-red-500'
        : 'border-gray-300 bg-white focus:border-teal-500'
    }`;

  return (
    <form
      noValidate
      onSubmit={handleSubmit}
      className="relative bg-white rounded-xl border border-gray-200 p-5 md:p-6"
    >
      {/* Honeypot: скрыто от людей и скринридеров, но значение уходит в теле как lead_hp. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="opacity-0 absolute -z-10 pointer-events-none w-0 h-0"
        style={{ left: '-9999px' }}
      />

      <div className="space-y-4">
        <div>
          <label
            htmlFor="leadmagnet-name"
            className="block text-sm font-medium text-gray-900 mb-1.5"
          >
            Имя <span className="text-teal-600">*</span>
          </label>
          <input
            id="leadmagnet-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) {
                setErrors((prev) => ({ ...prev, name: false }));
              }
            }}
            placeholder="Ваше имя"
            autoComplete="name"
            className={inputClass(errors.name)}
          />
          {errors.name && (
            <p className="text-xs text-red-600 mt-1.5">Укажите имя</p>
          )}
        </div>

        <div>
          <label
            htmlFor="leadmagnet-email"
            className="block text-sm font-medium text-gray-900 mb-1.5"
          >
            Email <span className="text-teal-600">*</span>
          </label>
          <input
            id="leadmagnet-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) {
                setErrors((prev) => ({ ...prev, email: false }));
              }
            }}
            placeholder="Ваш email"
            autoComplete="email"
            className={inputClass(errors.email)}
          />
          {errors.email && (
            <p className="text-xs text-red-600 mt-1.5">
              Проверьте адрес email
            </p>
          )}
        </div>

        <div
          className={`rounded-lg border px-3.5 py-3 ${
            errors.consent
              ? 'border-red-400 bg-red-50'
              : 'border-transparent bg-gray-50'
          }`}
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consentTransfer}
              onChange={(e) => {
                setConsentTransfer(e.target.checked);
                if (errors.consent) {
                  setErrors((prev) => ({ ...prev, consent: false }));
                }
              }}
              className="mt-0.5 w-4 h-4 accent-teal-600 flex-shrink-0 cursor-pointer"
            />
            <span className="text-xs text-gray-600 leading-relaxed">
              Согласен(на) с обработкой данных и подпиской на рассылку в
              соответствии с{' '}
              <a
                href="https://sivko.by/load/privacy-policy.pdf/"
                target="_blank"
                rel="noopener"
                className="text-teal-700 underline"
              >
                Политикой конфиденциальности
              </a>
            </span>
          </label>
          {errors.consent && (
            <p className="text-xs text-red-600 mt-2 pl-7">
              Отметьте согласие, чтобы продолжить
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-teal-600 text-white px-6 py-3.5 rounded-lg text-sm md:text-base font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {submitting ? 'Отправляем…' : 'Получить пособие на почту'}
        </button>
      </div>

      {status === 'error' && (
        <div className="mt-4 rounded-lg bg-red-50 border border-red-100 p-4">
          <p className="text-sm text-red-700 leading-relaxed mb-3">
            {ERROR_MESSAGE}
          </p>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="inline-flex items-center gap-2 bg-white border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            Попробовать снова
          </button>
        </div>
      )}
    </form>
  );
}